import {ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {
    OrganizationApiService,
    OrganizationResponse,
    ReferenceBookResponse,
    RoleApiService,
    UserApiService,
    UserAttributesApiService,
    UserResponse,
    UserTag,
} from '@npck/af-frontend-api';
import {IIN_PATTERN, MAX_LENGTH, RouterService, rxSpinner, Spinner} from '@npck/libs/commons';
import {SnackbarService} from '@npck/libs/ui-kit';
import {map, mergeMap} from 'rxjs';
import {PhoneNumberComponent} from '../../../../components/phone-number/phone-number.component';
import {AuthHolderService} from '../../../../domain/auth-holder.service';
import {getValidPhoneNumberOrNull, kzPhoneInfo, PhoneInfo} from '../../../../domain/phone-utils';
import {ORGANIZATION_ID, USER_ID} from '../../../../domain/query-params.keys';
import {Path} from '../../../../path';
import {AdminPanelTab} from '../../admin.component';

@Component({
    templateUrl: 'user-create-edit.component.html',
    styleUrls: ['user-create-edit.component.scss'],
    standalone: false
})
export class UserCreateEditComponent implements OnInit {
    @ViewChild('phoneNumberComponent') phoneNumberComponent: PhoneNumberComponent;
    Spinner = Spinner;
    Path = Path;
    userTypes: UserTag[];
    organizations: ReferenceBookResponse[] = [];
    form: FormGroup<CreateUserForm>;
    organizationsByType: ReferenceBookResponse[] = [];
    userId: string;
    organizationId: string;
    organization?: OrganizationResponse;
    readonlyOrganization?: ReadonlyOrganization;
    tabPathToTab: Record<string, AdminPanelTab> = {[Path.USERS]: 'tabUsers'};
    userResponse?: UserWithRoles;

    constructor(
        private formBuilder: FormBuilder,
        private userApiService: UserApiService,
        private roleApiService: RoleApiService,
        private userAttributesApiService: UserAttributesApiService,
        private organizationApiService: OrganizationApiService,
        private cdr: ChangeDetectorRef,
        private activatedRoute: ActivatedRoute,
        private routerService: RouterService,
        private snackbarService: SnackbarService,
        private authHolderService: AuthHolderService,
    ) {
        this.userId = this.activatedRoute.snapshot.paramMap.get(USER_ID);
        this.organizationId = this.activatedRoute.snapshot.paramMap.get(ORGANIZATION_ID);
        if (this.organizationId) {
            this.tabPathToTab = {[Path.ORGANIZATIONS]: 'tabOrganizations'};
        }
        this.userTypes = this.isOrganizationScoped ? [UserTag.PERSON] : [UserTag.SYSTEM, UserTag.PERSON];
        this.form = this.formBuilder.group({
            userType: new FormControl({
                value: null,
                disabled: this.userId,
            }, [Validators.required]),
        });
        if (this.organizationId) {
            Spinner.show('initial');
            this.organizationApiService.getOrganization(this.organizationId)
                .pipe(rxSpinner('initial'))
                .subscribe(organization => {
                    this.organization = organization;
                    this.readonlyOrganization = {type: organization.type, bin: organization.bin, nameRu: organization.nameRu};
                });
        } else if (this.isOrganizationScoped) {
            this.readonlyOrganization = {
                type: this.authHolderService.organizationType,
                bin: this.authHolderService.organizationBin,
                nameRu: this.authHolderService.currentOrgInfo?.name_ru || this.authHolderService.organizationBin,
            };
        }
        if (this.userId) {
            Spinner.show('initial');
            userAttributesApiService.getUser(this.userId)
                .pipe(
                    mergeMap(user => this.roleApiService.getUserRoles(user.id)
                        .pipe(map(roles => <UserWithRoles>{user: user, roles: roles}))))
                .subscribe(response => {
                    this.userResponse = response;
                    this.form.controls.userType.setValue(response?.user?.tag);
                });
        }

        this.form.controls.userType.valueChanges
            .subscribe(() => this.updateForm());

    }

    get isOrganizationScoped(): boolean {
        return this.authHolderService.isOrganizationScopedAdmin;
    }

    ngOnInit(): void {
        if (this.isOrganizationScoped && !this.userId) {
            this.form.controls.userType.setValue(UserTag.PERSON);
        }
    }

    filterUserIdPath = (path: string): boolean => {
        return path === this.userId;
    };

    submit(): void {
        if (this.userResponse) {
            this.userApiService.updateUser(this.userResponse.user.id,
                {
                    tag: this.userResponse.user.tag,
                    username: this.userResponse.user.username,
                    lastName: this.form.value.lastName,
                    firstName: this.form.value.firstName,
                    iin: this.form.value.iin,
                    phoneNumber: this.getPhoneNumber(),
                    organizationType: this.form.controls.organizationType?.getRawValue(),
                    bin: this.form.controls.organizationBin?.getRawValue(),
                    email: this.form.value.email,
                    emailNotify: this.form.value.enableEmailNotification,
                    enabled: this.form.value.enableUacAccess,
                    dn: this.form.value.dnLdapName,
                })
                .pipe(
                    mergeMap(() => this.roleApiService.deleteUserRoles(this.userResponse.user.id, {roles: this.userResponse.roles})),
                    mergeMap(() => this.roleApiService.updateUserRoles(this.userResponse.user.id, {roles: this.form.value.compositeRoles})),
                    rxSpinner('submit'),
                )
                .subscribe(() => {
                    this.routerService.navigateToTab<AdminPanelTab>(Path.ADMIN_PANEL, 'tabUsers');
                    this.snackbarService.showSnackBar('i18n_af_snackbarMessageUserUpdated', 'success', false, {
                        fullName: [this.userResponse.user.lastName, this.userResponse.user.firstName].filter(n => !!n).join(' '),
                        email: this.userResponse.user.email,
                    });
                });
        } else {
            this.userApiService.createUser(
                {
                    tag: this.form.value.userType,
                    lastName: this.form.value.lastName,
                    firstName: this.form.value.firstName,
                    iin: this.form.value.iin,
                    phoneNumber: this.getPhoneNumber(),
                    organizationType: this.form.controls.organizationType?.getRawValue(),
                    bin: this.form.controls.organizationBin?.getRawValue(),
                    username: this.form.value.username,
                    email: this.form.value.email,
                    emailNotify: this.form.value.enableEmailNotification,
                    enabled: this.form.value.enableUacAccess,
                    dn: this.form.value.dnLdapName,
                })
                .pipe(
                    mergeMap(response => this.roleApiService.updateUserRoles(response.id, {roles: this.form.value.compositeRoles})),
                    rxSpinner('submit'),
                )
                .subscribe(() => {
                    this.navigateBack();
                    this.snackbarService.showSnackBar('i18n_af_snackbarMessageUserCreated', 'success', false, {
                        fullName: [this.form.value.lastName, this.form.value.firstName].filter(n => !!n).join(' '),
                        email: this.form.value.email,
                    });
                });
        }
    }

    navigateBack(): void {
        if (this.organizationId) {
            this.routerService.navigateToTab<AdminPanelTab>(
                `${Path.ADMIN_PANEL_ORGANIZATIONS}/${this.organizationId}`, 'tabOrganizations');
            return;
        }
        this.routerService.navigateToTab<AdminPanelTab>(Path.ADMIN_PANEL, 'tabUsers');
    }

    private updateForm(): void {
        this.removeControllers();
        this.form.addControl('organizationType', new FormControl(null, [Validators.required]));

        this.form.controls.organizationType.valueChanges
            .subscribe(value => {
                this.organizationsByType = this.organizations.filter(item => item.code1 === value);
                if (!this.organizationId) {
                    this.form.controls.organizationBin.enable();
                }
            });

        this.form.addControl('organizationBin', new FormControl(this?.userResponse?.user?.bin, [Validators.required]));
        this.form.addControl('compositeRoles', new FormControl<string[]>(null, [Validators.required]));

        this.form.addControl('username',
            new FormControl({value: this.userResponse?.user?.username, disabled: !!this.userResponse?.user?.username},
                [Validators.required, Validators.maxLength(MAX_LENGTH)]),
        );
        this.form.addControl('email', new FormControl(this.userResponse?.user?.email, [Validators.required, Validators.email]));
        this.form.addControl('enableEmailNotification', new FormControl(this.userResponse?.user?.emailNotify, [Validators.required]));
        this.form.addControl('enableUacAccess',
            new FormControl(this.userResponse ? this.userResponse.user.isDeleted !== true : null, [Validators.required]));

        if (this.readonlyOrganization) {
            this.form.controls.organizationType.setValue(this.readonlyOrganization.type, {emitEvent: false});
            this.form.controls.organizationType.disable({emitEvent: false});
            this.form.controls.organizationBin.setValue(this.readonlyOrganization.bin, {emitEvent: false});
            this.form.controls.organizationBin.disable({emitEvent: false});
        }
        if (this.userResponse && this.organizations) {
            this.form.controls.organizationType.setValue(this.userResponse?.user?.organizationType);
        }
        if (!this.form.contains('userType')) {
            this.form.addControl('userType', new FormControl(this.userResponse?.user?.tag));
            this.form.controls.userType.enable({emitEvent: false});
        }
        if (this.form.value.userType === UserTag.PERSON) {
            this.createPersonUserForm();
        } else {
            this.form.addControl('dnLdapName', new FormControl(this.userResponse?.user.dn, [Validators.required]));
        }
        this.cdr.detectChanges();
        Spinner.hide('initial');
    }

    private createPersonUserForm(): void {
        this.form.addControl('iin', new FormControl(this.userResponse?.user?.iin, [Validators.required, Validators.pattern(IIN_PATTERN)]));
        this.form.addControl('lastName',
            new FormControl(this.userResponse?.user?.lastName, [Validators.required, Validators.maxLength(MAX_LENGTH)]));
        this.form.addControl('firstName',
            new FormControl(this.userResponse?.user?.firstName, [Validators.required, Validators.maxLength(MAX_LENGTH)]));
        this.form.addControl('phone', new FormControl(this.userResponse?.user?.phoneNumber ?? null, [Validators.required]));
        this.form.addControl('selectedPhoneInfo', new FormControl(null));
        const phone = this.userResponse?.user?.phoneNumber ?? null;
        if (this.userResponse?.user?.phoneNumber) {
            this.form.controls.selectedPhoneInfo.setValue(kzPhoneInfo, {emitEvent: false});
            this.form.controls.phone.setValue(phone, {emitEvent: false});
            queueMicrotask(() => {
                this.form.controls.phone.setValue(phone, {emitEvent: false});
            });
        }
    }

    private removeControllers(): void {
        Object.keys(this.form.controls)
            .filter((controlName): controlName is keyof CreateUserForm => controlName !== 'userType')
            .forEach(controlName => this.form.removeControl(controlName, {emitEvent: true}));
    }

    private getPhoneNumber(): string | null {
        const isPerson =
            String(this.form.value.userType ?? this.userResponse?.user?.tag ?? '')
                .toLowerCase() === String(UserTag.PERSON).toLowerCase();
        if (!isPerson) {
            return null;
        }
        return getValidPhoneNumberOrNull(
            this.form.value.phone,
            this.form.value.selectedPhoneInfo,
        );
    }
}

interface UserWithRoles {
    user: UserResponse;
    roles: string[];
}

interface ReadonlyOrganization {
    type: string;
    bin: string;
    nameRu: string;
}

interface CreateUserForm {
    userType: FormControl<UserTag>;
    compositeRoles?: FormControl<string[]>;
    organizationType?: FormControl<string>
    organizationBin?: FormControl<string>;
    email?: FormControl<string>;
    username?: FormControl<string>;
    enableEmailNotification?: FormControl<boolean>
    enableUacAccess?: FormControl<boolean>
    // Technical User controls
    dnLdapName?: FormControl<string>;
    // Person User controls
    iin?: FormControl<string>;
    lastName?: FormControl<string>;
    firstName?: FormControl<string>;
    phone?: FormControl<string>;
    selectedPhoneInfo?: FormControl<PhoneInfo>;
}
